import React, { useState } from "react";
import {
  Grid,
  Typography,
  Card,
  CardContent,
  Container,
  Paper,
  List,
  ListItem,
  ListItemText,
  Box,
  Collapse,
  IconButton,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

const DissertationServices: React.FC = () => {
  const [activeIndex, setActiveIndex] = useState<number>(0);

  const otherServices = [
    { title: "Dissertation", route: "/dissertation-writing-services" },
    { title: "Coursework", route: "/coursework-writing" },
    { title: "Data Analytics", route: "/data-analysis-services" },
    { title: "Project writing services", route: "/project-report-writing" },
    { title: "Publication", route: "/publication-services" },
    { title: "Literature Review", route: "/literature-review-writing" },
    { title: "Research methodology", route: "/research-methodology-services" },
  ];

  const data = {
    icon: "ds6.webp",
    title: "Manuscript",
    image: "ds6.webp",
    description:
      "Improve your academic presentation skills with guided coaching on structure, design, and delivery.",
    additionalInfo: [
      {
        title: "Manuscript Editing",

        icon1: "",
        description:
          "Our Manuscript Editing service ensures your work is clear, polished, and ready for submission. We refine grammar, punctuation, structure, and flow while maintaining your original voice, helping your manuscript meet academic and publication standards. This ensures your research is presented professionally and enhances its chances of acceptance.",

        cards: [
          {
            title: "Grammar and Punctuation Check",

            description:
              "Ensuring that your manuscript is free from grammatical errors and punctuation mistakes is crucial. This service focuses on correcting these issues to make your writing clear and professional, enhancing its readability.",
          },
          {
            title: "Structural Refinement",

            description:
              "A well-structured manuscript is easier to follow. We help organize your ideas logically, ensuring that the flow of information is smooth and each section supports your main arguments effectively.",
          },
          {
            title: "Clarity and Style Improvement",

            description:
              "We enhance the clarity of your writing by simplifying complex sentences and improving the overall tone and style. This makes your manuscript easier to understand while preserving your original voice.",
          },
          {
            title: "Formatting for Journal Standards",

            description:
              "Every journal has specific formatting requirements. We ensure your manuscript adheres to the necessary guidelines, including citation styles, headings, and figure placement, so that it meets submission standards.",
          },
        ],
      },
      {
        title: "Formatting",

        icon1: "",
        description:
          "Our Formatting service ensures your manuscript meets journal or academic guidelines, including citations, references, headings, and layout. We make sure everything is consistent and submission-ready, avoiding formatting-related rejections and ensuring a professional presentation. This attention to detail enhances the overall quality and credibility of your work.",

        cards: [
          {
            title: "Citation and Reference Management",

            description:
              "Proper citation and reference formatting are crucial for academic integrity. Our service ensures that all sources are accurately cited according to the required style guide (APA, MLA, Chicago, etc.), making it easier for readers to locate and verify your references.",
          },
          {
            title: "Consistency in Formatting",

            description:
              "Consistency throughout your manuscript enhances professionalism and readability. We ensure uniformity in font size, spacing, margins, and heading styles, creating a cohesive look that aligns with submission standards.",
          },
          {
            title: "Table and Figure Formatting",

            description:
              "Well-formatted tables and figures are essential for conveying complex data clearly. We assist in organizing and formatting visual elements, including captions and legends, ensuring they meet the required specifications for clarity and presentation.",
          },
          {
            title: "Final Layout Review",

            description:
              "A thorough final layout review is vital before submission. Our service includes a comprehensive check of the overall manuscript layout to ensure that all elements are aligned correctly and that the document is polished and ready for publication.",
          },
        ],
      },
      {
        title: "Plagiarism Check",

        icon1: "",
        description:
          "Our Plagiarism Check service ensures your manuscript's originality by scanning for copied content and ensuring all sources are properly cited. This helps maintain academic integrity and ensures your work is plagiarism-free for submission, reducing the risk of rejection due to originality concerns. By addressing these issues early, we help you present a credible and ethical piece of research.",

        cards: [
          {
            title: "Advanced Plagiarism Detection",

            description:
              "We use industry-leading plagiarism detection tools to scan your manuscript for any unintentional overlaps with existing published work. This ensures that all content is original and free from copied material.",
          },
          {
            title: "Proper Citation and Referencing",

            description:
              "Our service ensures that all borrowed ideas, quotes, and data are properly cited according to the required academic style. This helps avoid plagiarism while giving due credit to original sources.",
          },
          {
            title: "Ethical Compliance",

            description:
              "Ensuring the ethical integrity of your work is crucial in academic writing. We help you meet ethical standards by verifying that your manuscript is plagiarism-free, thus upholding your academic credibility.",
          },
          {
            title: "Pre-Submission Peace of Mind",

            description:
              "A thorough plagiarism check before submission gives you peace of mind. You can confidently submit your manuscript knowing it has been checked for originality and adheres to the highest standards of academic integrity.",
          },
        ],
      },
      {
        title: "Peer Review Support",

        icon1: "",
        description:
          "Our Peer Review Support service helps you address reviewer feedback effectively, guiding you through revisions to improve your manuscript. We ensure your responses are clear and professional, increasing your chances of acceptance. Our expert team also provides insights on common reviewer concerns, helping you strengthen your submission for a successful outcome.",

        cards: [
          {
            title: "Comprehensive Feedback Analysis",

            description:
              "We provide a thorough analysis of reviewer comments to help you understand their concerns and suggestions. This ensures you address all feedback effectively, enhancing the overall quality of your manuscript.",
          },
          {
            title: "Effective Revision Strategies",

            description:
              "Our team offers tailored revision strategies that align with the reviewer’s expectations. We guide you in making meaningful changes that improve your manuscript while preserving your original research intent.",
          },
          {
            title: "Response Letter Preparation",

            description:
              "Crafting a clear and professional response letter is essential for the peer review process. We assist in drafting responses that directly address reviewer comments, demonstrating your willingness to improve your work.",
          },
          {
            title: "Final Quality Check",

            description:
              "Before resubmission, we conduct a final quality check to ensure your revised manuscript meets academic standards. This step guarantees that your work is polished and ready for successful publication.",
          },
        ],
      },
      {
        title: "Journal Selection Assistance",

        icon1: "",
        description:
          "Our Journal Selection Assistance service helps you identify the most suitable academic journals for your manuscript. We analyze your research topic, scope, and impact to recommend journals that align with your work. This service enhances your chances of acceptance by ensuring your submission matches the journal’s focus and audience.",

        cards: [
          {
            title: "Tailored Journal Recommendations",

            description:
              "We provide personalized recommendations for journals that best fit your research focus and objectives. By analyzing your manuscript's content, we help you select journals that align with your work, increasing the likelihood of acceptance.",
          },
          {
            title: "Impact Factor Consideration",
            description:
              "Understanding a journal's impact factor is crucial for publication success. We help you evaluate potential journals based on their impact factor, ensuring you choose reputable journals that enhance the visibility of your research.",
          },
          {
            title: "Submission Guidelines Overview",
            description:
              "Each journal has specific submission guidelines. Our service includes a detailed overview of these requirements, ensuring you understand formatting, length, and other criteria to make your submission process seamless.",
          },
          {
            title: "Strategic Submission Planning",

            description:
              "We assist you in developing a strategic submission plan, including timelines and potential journal options based on your goals. This planning helps you navigate the submission process effectively, maximizing your chances of publication success.",
          },
        ],
      },
      {
        title: "Translation Services",

        icon1: "",
        description:
          "Our Translation Services provide accurate and culturally sensitive translations for academic manuscripts, research papers, and other written content. We ensure that your ideas are effectively conveyed in the target language, maintaining the original meaning and tone. Our team of skilled linguists is proficient in various fields, ensuring high-quality translations tailored to your specific needs.",
        cards: [
          {
            title: "Accurate Language Translation",

            description:
              "We specialize in delivering precise translations that capture the nuances of your original text. Our translators are fluent in both the source and target languages, ensuring that the meaning, context, and intent of your work are preserved.",
          },
          {
            title: " Subject Matter Expertise",
            description: "Using visuals to enhance your message.",
          },
          {
            title: "Cultural Sensitivity",
            description:
              "Translation goes beyond words; it involves understanding cultural contexts. Our service ensures that translations are culturally appropriate, adapting idioms, references, and examples to resonate with the target audience while preserving the essence of your content",
          },
          {
            title: " Proofreading and Quality Assurance",
            description:
              "We offer proofreading and quality assurance checks to guarantee the highest standards in your translated document. Our process includes thorough reviews to ensure clarity, coherence, and accuracy, providing you with a polished final product ready for submission or publication.",
          },
        ],
      },
      {
        title: "Figures and Table Design",

        icon1: "",
        description:
          "Our Figures and Table Design service helps you create visually compelling and scientifically accurate illustrations, charts, graphs, and tables for academic assignments and research papers. We ensure that your data is presented clearly and effectively, making it easy for readers to interpret and understand your findings.",

        cards: [
          {
            title: "Custom Figure Creation",
            description:
              "We design custom figures based on your research data, using tools and techniques that enhance clarity and precision. Our team ensures that your figures are tailored to meet the specific requirements of your academic field, making your results visually engaging and easy to understand.",
          },
          {
            title: "Data-Driven Table Formatting",
            description:
              "Our service includes professional formatting of tables that organize and present your data clearly. We focus on creating tables that not only look clean but also adhere to academic standards, helping you present complex data in a structured, readable manner.",
          },
          {
            title: "Adherence to Journal Guidelines",
            description:
              "Every journal has its own guidelines for figures and tables. We ensure that your designs meet these specifications, including resolution, size, color formats, and labeling conventions, making your submission process smoother and increasing your chances of acceptance",
          },
          {
            title: "Visual Consistency and Cohesion",

            description:
              "We ensure visual consistency throughout your figures and tables, using standardized fonts, labels, and color schemes. This cohesive design approach strengthens the overall presentation of your research, making it more professional and polished for academic publication or review.",
          },
        ],
      },
    ],
  };
  const handleServiceClick = (route: string) => {
    window.location.href = route;
  };

  const handleHeadingClick = (index: number) => {
    setActiveIndex(activeIndex === index ? 0 : index);
  };

  return (
    <Container maxWidth={false} disableGutters>
      <Grid
        container
        spacing={0}
        sx={{
          backgroundColor: "#172554",
          padding: { sm: 0, lg: 3 },
          display: "flex",
          flexDirection: "row-reverse",
        }}
        className="component"
      >
        {/* Left Side - Other Services List (Desktop) and Expandable Headings (Mobile) */}
        <Grid item xs={12} md={3}>
          {/* Desktop View */}
          <Box sx={{ display: { xs: "none", md: "block" } }}>
            <List>
              {data.additionalInfo.map((info, index) => (
                <ListItem
                  button
                  key={index}
                  onClick={() => handleHeadingClick(index)}
                  selected={activeIndex === index}
                  sx={{ color: "white" }}
                >
                  <ListItemText primary={info.title} />
                </ListItem>
              ))}
              <Typography
                sx={{ textAlign: "center", color: "white", fontWeight: "bold" }}
              >
                Our Other Services
              </Typography>
              {otherServices.map((service, index) => (
                <ListItem
                  button
                  key={index}
                  onClick={() => handleServiceClick(service.route)}
                  sx={{ color: "white" }}
                >
                  <ListItemText primary={service.title} />
                </ListItem>
              ))}
            </List>
          </Box>

          {/* Mobile View - Expandable Headings */}
          <Box sx={{ display: { xs: "block", md: "none" } }}>
            {data.additionalInfo.map((info, index) => (
              <Box key={index}>
                <ListItem
                  button
                  onClick={() => handleHeadingClick(index)}
                  sx={{
                    color: "white",
                    borderBottom: "1px solid rgba(255,255,255,0.1)",
                  }}
                >
                  <ListItemText primary={info.title} />
                  <IconButton sx={{ color: "white" }}>
                    {activeIndex === index ? (
                      <ExpandLessIcon />
                    ) : (
                      <ExpandMoreIcon />
                    )}
                  </IconButton>
                </ListItem>
                <Collapse
                  in={activeIndex === index}
                  timeout="auto"
                  unmountOnExit
                >
                  <Box
                    sx={{
                      backgroundColor: "#f3f4f6",
                      color: "black",
                      padding: 2,
                    }}
                  >
                    <Typography variant="body2" sx={{ marginBottom: 2 }}>
                      {info.description}
                    </Typography>
                    {info.cards.map((card, cardIndex) => (
                      <Card
                        key={cardIndex}
                        sx={{
                          marginBottom: 2,
                          backgroundColor: "#172554",
                          color: "white",
                        }}
                      >
                        <CardContent>
                          <Typography variant="h6">
                            <i
                              className="fa-solid fa-circle-check"
                              style={{ color: "#f97316", paddingRight: "10px" }}
                            ></i>
                            {card.title}
                          </Typography>
                          <Typography variant="body2">
                            {card.description}
                          </Typography>
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                </Collapse>
              </Box>
            ))}
          </Box>
        </Grid>

        {/* Right Side - Dissertation Content (Desktop Only) */}
        <Grid item md={9} sx={{ display: { xs: "none", md: "block" } }}>
          <Paper
            elevation={3}
            sx={{
              padding: 3,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              backgroundColor: "#f3f4f6",
              boxShadow: "none",
              borderRadius: "0px",
            }}
          >
            {activeIndex !== null && (
              <>
                <Typography variant="h5" sx={{ marginBottom: 2 }}>
                  {data.additionalInfo[activeIndex].title}
                </Typography>
                <Box sx={{ marginBottom: 2 }}></Box>
                <Typography sx={{ marginBottom: 4, textAlign: "center" }}>
                  {data.additionalInfo[activeIndex].description}
                </Typography>

                <Grid
                  container
                  direction="column"
                  spacing={2}
                  sx={{
                    backgroundColor: "#172554",
                    color: "white",
                    borderRadius: "4px",
                    maxWidth: { lg: "75%", xs: "100%" },
                  }}
                >
                  {data.additionalInfo[activeIndex].cards.map((card, index) => (
                    <Grid item key={index}>
                      <Card
                        sx={{
                          boxShadow: "none",
                          backgroundColor: "transparent",
                          color: "white",
                        }}
                      >
                        <CardContent
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                          }}
                        >
                          <Typography variant="h6" color={"white"}>
                            <i
                              className="fa-solid fa-circle-check"
                              style={{ color: "#f97316", paddingRight: "10px" }}
                            ></i>
                            {card.title}
                          </Typography>
                          <Typography variant="body2" textAlign={"center"}>
                            {card.description}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default DissertationServices;
